import React from 'react';
import HTML5Backend from 'react-dnd-html5-backend';
import { TouchTransition } from 'react-dnd-multi-backend';
import TouchBackend from 'react-dnd-touch-backend';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { getQueryStringServiceFromAngular } from '../../../common/react/QueryString';
import { DataSources } from '../../../content/resources/Datasources';
import { MobileAppBanner } from '../../../mobile/components/MobileAppBanner';
import { StartPageService } from '../../../settings/services/StartPageService';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { shouldResetPreviousUrl } from '../../../ts/helpers/UrlHelpers';
import { SiteSwitchService } from '../../../ts/siteManagement/services/SiteSwitchService';
import { postLoginNotification } from '../../../zeus/PostLoginNotification';
import { siteExpirationNotification } from '../../../zeus/SiteExpirationNotification';
import { incrementRouterSpaceReactKey } from '../../actions/spaces/IncrementRouterSpaceReactKeyAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { setPreviousUrl } from '../../actions/spaces/SetPreviousUrlReducerAction';
import { getAppStore } from '../../AppStore';
import { bindDisplaySettingsScopeToReduxAndSetInitialValues } from '../../content/DisplaySettingsReduxBindings';
import { bindDisplaySettingsScopeToUrlAndSetInitialValues } from '../../content/DisplaySettingsUrlBindings';
import { ConnectedLeftNav } from '../../layout/ConnectedLeftNav';
import { ConnectedPageHeader } from '../../layout/ConnectedPageHeader';
import { ConnectedSkipBarProvider } from '../../layout/SkipBarProvider';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { selectSpaceInfo } from '../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { isMetricSpace } from '../../state/SpaceInfo';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { CustomDragLayer } from '../CustomDragLayer';
import { ServerSpaceRouter } from '../ServerSpaces/ServerSpaceRouter';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { BasePathsUtils } from './BasePathsUtils';
import { SiteSpaceRouter } from './SiteSpaceRouter';

export interface MainOwnProps extends ReactDirectiveProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
  readonly fromUnitTests?: boolean; // Hooks don't work well with shallow tests so dirty workaround
}

interface MainPropsFromAngular {
  readonly $location: ng.ILocationService;
}

export interface MainStateProps {
  readonly urls: SpacesSiteUrls;
  readonly isMetricSpace: boolean;
}

export interface MainDispatchProps {
  readonly setPreviousUrl: (url: string) => void;
  readonly incrementRouterSpaceReactKey: () => void;
  readonly requestSpaceContents: () => void;
}

export type MainProps = MainOwnProps & MainPropsFromAngular & MainStateProps & MainDispatchProps;

export const MainComponent: React.FunctionComponent<MainProps> = props => {

  const siteNameFromCurrentPath = parseOutSiteUrlName(props.routeLocationPath);
  const siteNameFromRedux = parseOutSiteUrlName(props.urls.homePath());
  const [path, setPath] = React.useState(siteNameFromCurrentPath === siteNameFromRedux ? props.routeLocationPath : '');

  React.useEffect(() => {
    if (!props.fromUnitTests) {
      const queryString = getQueryStringServiceFromAngular(props.$location);
      bindDisplaySettingsScopeToReduxAndSetInitialValues(props.angularContext.scope as any, getAppStore());
      bindDisplaySettingsScopeToUrlAndSetInitialValues(props.angularContext.scope as any, getAppStore(), queryString);
    }
  }, [ props.fromUnitTests ]);

  React.useEffect(() => {
    if (postLoginNotification) {
      postLoginNotification.showNotificationsIfEnabled();
    }
  }, [ postLoginNotification ]);

  React.useEffect(() => {
    if (!props.fromUnitTests && siteExpirationNotification) {
      siteExpirationNotification.showNotificationsIfEnabled();
    }
  }, [ props.fromUnitTests, siteExpirationNotification ]);

  React.useEffect(() => {
    if (props.routeLocationPath !== path) {
      const siteNameFromCurrentPath = parseOutSiteUrlName(props.routeLocationPath);
      const isChangingSites = parseOutSiteUrlName(path) !== siteNameFromCurrentPath;
      if (isChangingSites && !_.isUndefined(siteNameFromCurrentPath)) {
        SiteSwitchService.switchIfNeeded(siteNameFromCurrentPath)
          .finally(() => {
            setPath(props.routeLocationPath);
          });
      } else {
        setPath(props.routeLocationPath);
      }

      // Includes resetting when navigating to /server which isChangingSites will catch
      if (shouldResetPreviousUrl(props.routeLocationPath, isChangingSites)){
        props.setPreviousUrl('');
      }
    }
  }, [props.routeLocationPath]);

  if (props.fromUnitTests) { return null; }

  const HTML5toTouch = {
    backends: [
      {
        backend: HTML5Backend
      },
      {
        backend: TouchBackend,
        options: {enableMouseEvents: true, enableKeyboardEvents: true, delayTouchStart: 200},
        transition: TouchTransition
      }
    ]
  };

  const constrainedClass = props.isMetricSpace ? '' : 'tb-main-constrained';
  const defaultRedirectPath = getDefaultRedirectPath(props.routeLocationPath);
  return (
    <div className={'tb-column-flex-parent tb-fill'}>
      <ConnectedSkipBarProvider />
      <MobileAppBanner />
      <div className={'tb-flex-fill ' + constrainedClass}>
        <div className={'tb-row-flex-parent tb-full-height'}>
          <ConnectedLeftNav />
          <div className={'tb-flex-fill tb-column-flex-parent tb-main-content'}>
            <ConnectedPageHeader angularContext={props.angularContext} datasourcesResource={DataSources}/>
            <div className={'tb-flex-fill'}>
                <CustomDragLayer/>
                <Switch>
                  <Route exact from={props.urls.defaultPath()}>
                    <Redirect exact from={props.urls.defaultPath()} to={defaultRedirectPath}/>
                  </Route>
                  <Route exact from={'/'}>
                    <Redirect exact from={props.urls.defaultPath()} to={defaultRedirectPath}/>
                  </Route>
                  <Route path={'/server'}>
                    <ServerSpaceRouter
                      angularContext={props.angularContext}
                      routeLocationPath={props.routeLocationPath}
                      routeLocationSearch={props.routeLocationSearch}
                    />
                  </Route>
                  <Route>
                    <SiteSpaceRouter
                      isBusy={parseOutSiteUrlName(path) !== siteNameFromCurrentPath}
                      siteName={siteNameFromCurrentPath}
                      angularContext={props.angularContext}
                      routeLocationPath={path} // Don't want to render until the site switch is done and path has been updated
                      routeLocationSearch={props.routeLocationSearch}
                    />
                  </Route>
                </Switch>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const parseOutSiteUrlName = (path: string): string  => {
  if (path.startsWith('/site/')) {
    path = path.substring(6); // chop off the initial '/site/'
    const endSiteNamePattern = new RegExp('[/?]');
    const endSiteNameIndex = path.search(endSiteNamePattern);
    if (endSiteNameIndex === -1) {
      return path === '' ? undefined : path;
    }
    const siteUrlName = path.substring(0, endSiteNameIndex);
    return siteUrlName === '' ? undefined : siteUrlName;
  }

  // if its a valid site space path return empty string to indicate default
  const urls = new SpacesSiteUrls({ siteUrlName: '' });
  const basePaths = BasePathsUtils.getWatchedBasePathsForSite(urls);
  if (_.some(basePaths, (basePath) => path.startsWith(basePath))) {
    return '';
  }

  // these should be /server/ paths or invalid paths
  return undefined;
};

const getDefaultRedirectPath = (routeLocationPath: string): string => {
  const siteUrlName = parseOutSiteUrlName(routeLocationPath);
  if (!_.isUndefined(siteUrlName) && siteUrlName.length > 0) {
    return StartPageService.getCurrentUserStartPageForSite(siteUrlName);
  } else {
    return StartPageService.getCurrentUserStartPageForCurrentSite();
  }
};

export namespace MainAngularProvider {
  function getAngularServicesWithServiceLocator() {
    return {
      $location: ServiceLocator.get<ng.ILocationService>('$location'),
    };
  }

  function selectPropsFromAngularServices(angularServices: MainPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<MainOwnProps, MainPropsFromAngular, MainPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const mapStateToProps: MapStateToProps<MainStateProps> = (state: AppState) => ({
  urls: siteUrlsSelector(state),
  isMetricSpace: isMetricSpace(selectSpaceInfo(state))
});

const mapDispatchToProps: MapDispatchToProps<MainDispatchProps> = (dispatch) => bindActionCreators({
  incrementRouterSpaceReactKey,
  setPreviousUrl,
  requestSpaceContents,
}, dispatch);

export const ConnectedMainComponent: React.ComponentClass<MainOwnProps & MainPropsFromAngular>
  = connect(mapStateToProps, mapDispatchToProps)(MainComponent);

export const Main: React.ComponentType<MainOwnProps> = MainAngularProvider.HOC(ConnectedMainComponent);
